import { TextNode, EditorConfig, LexicalNode, NodeKey, SerializedTextNode, Spread } from "lexical";

export type MarkerPayload = {
    markerId: string;
    title: string;
    /** Clip-local time (seconds) to seek to when clicked (computed at render time). */
    timeSec: number;
};

export type SerializedMarkerNode = Spread<
    {
        type: "marker";
        version: 1;
        payload: MarkerPayload;
    },
    SerializedTextNode
>;

/**
 * MarkerNode - A styled text node for transcript markers (chapters/headers).
 * Markers are transcript-only navigation aids and do not affect captions/export.
 */
export class MarkerNode extends TextNode {
    __payload: MarkerPayload;

    static getType(): string {
        return "marker";
    }

    static clone(node: MarkerNode): MarkerNode {
        return new MarkerNode(node.__payload.title, { ...node.__payload }, node.__key);
    }

    constructor(text: string, payload: MarkerPayload, key?: NodeKey) {
        super(text, key);
        this.__payload = payload;
    }

    createDOM(_config: EditorConfig): HTMLElement {
        const container = document.createElement("span");
        container.style.fontFamily = 'Inter, sans-serif';
        container.style.fontSize = '16px';
        container.style.fontWeight = '700';
        container.style.color = '#111827';
        container.style.display = 'inline-flex';
        container.style.alignItems = 'center';
        container.style.gap = '0px';
        container.style.marginTop = '18px';
        container.style.marginBottom = '10px';
        container.style.userSelect = 'none';
        container.style.cursor = 'pointer';
        container.style.padding = '4px 6px';
        container.style.borderRadius = '6px';
        container.style.transition = 'background-color 0.15s ease';
        container.classList.add('transcript-marker');

        const textSpan = document.createElement("span");
        textSpan.textContent = this.__payload.title || this.__text;
        textSpan.classList.add("marker-text");
        container.appendChild(textSpan);

        const deleteBtn = document.createElement("span");
        deleteBtn.innerHTML = "×";
        deleteBtn.style.fontSize = "16px";
        deleteBtn.style.fontWeight = "normal";
        deleteBtn.style.color = "#9CA3AF";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.style.opacity = "0";
        deleteBtn.style.transition = "opacity 0.15s ease, color 0.15s ease";
        deleteBtn.style.marginLeft = "8px";
        deleteBtn.style.lineHeight = "1";
        deleteBtn.style.padding = "0 2px";
        deleteBtn.classList.add("marker-delete-btn");
        deleteBtn.dataset.markerId = this.__payload.markerId;

        container.addEventListener("mouseenter", () => {
            deleteBtn.style.opacity = "1";
            container.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
        });
        container.addEventListener("mouseleave", () => {
            deleteBtn.style.opacity = "0";
            container.style.backgroundColor = "transparent";
        });
        deleteBtn.addEventListener("mouseenter", () => {
            deleteBtn.style.color = "#EF4444";
        });
        deleteBtn.addEventListener("mouseleave", () => {
            deleteBtn.style.color = "#9CA3AF";
        });

        container.appendChild(deleteBtn);

        container.addEventListener("mousedown", (e) => {
            e.preventDefault();
        });

        return container;
    }

    updateDOM(prevNode: MarkerNode, dom: HTMLElement): boolean {
        // Keep the same DOM structure; just update title text if needed.
        if (prevNode.__payload.title !== this.__payload.title || prevNode.__text !== this.__text) {
            const textEl = dom.querySelector(".marker-text");
            if (textEl) textEl.textContent = this.__payload.title || this.__text;
        }
        // Update markerId dataset on delete btn.
        if (prevNode.__payload.markerId !== this.__payload.markerId) {
            const btn = dom.querySelector(".marker-delete-btn") as HTMLElement | null;
            if (btn) btn.dataset.markerId = this.__payload.markerId;
        }
        return false;
    }

    static importJSON(serializedNode: SerializedMarkerNode): MarkerNode {
        const node = new MarkerNode(serializedNode.text, serializedNode.payload);
        node.setFormat(serializedNode.format);
        node.setDetail(serializedNode.detail);
        node.setMode(serializedNode.mode);
        node.setStyle(serializedNode.style);
        return node;
    }

    exportJSON(): SerializedMarkerNode {
        return {
            ...super.exportJSON(),
            type: "marker",
            version: 1,
            payload: this.__payload,
        };
    }

    getMarkerId(): string {
        return this.__payload.markerId;
    }

    getTitle(): string {
        return this.__payload.title;
    }

    getTimeSec(): number {
        return Number(this.__payload.timeSec ?? 0);
    }
}

export function $createMarkerNode(payload: MarkerPayload): MarkerNode {
    return new MarkerNode(payload.title || "Marker", payload);
}

export function $isMarkerNode(node?: LexicalNode | null): node is MarkerNode {
    return node instanceof MarkerNode;
}

